import S from 'common/service/sanctuary';
import React from 'react';
import {
  func,
  arrayOf,
} from 'prop-types';
import { isEmpty } from 'ramda';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';
import Button from 'common/mdc/button';
import Icon from 'common/mdc/icon';
import { ToolbarRow, ToolbarSection } from 'common/mdc/toolbar';
import Menu, { MenuItem, MenuWrapper } from 'common/mdc/menu';
import { CheckboxField } from 'common/mdc/form';
import { LABEL_AFTER } from 'common/mdc/form/checkbox';
import { EmptyTitle } from 'common/mdc/wrappers/empty';
import Criteria, { criteriaShape } from './criteria';

const messages = defineMessages({
  clearAll: {
    id: 'components.search.advanced.clearAll',
    defaultMessage: 'Clear all',
  },
  addCriteria: {
    id: 'components.search.advanced.addCriteria',
    defaultMessage: 'Add criteria',
  },
  noCriteria: {
    id: 'components.search.advanced.noCriteria',
    defaultMessage: 'No criteria',
  },
});

const AdvancedSearch = ({
  className,
  criteriaConfig,
  hasCriteria,
  getCriteriaState,
  addCriteria,
  removeCriteria,
  applyCriteria,
  addConstraint,
  removeConstraint,
  hasConstraints,
  clearConstraints,
  setOperator,
  setValue,
  clear,
}) => {
  const criteria$ = S.filter(
    S.compose(hasCriteria, S.prop('property')),
    criteriaConfig,
  );

  return (
    <ToolbarRow className="advanced-search">
      <ToolbarSection align={ToolbarSection.ALIGN_START}>
        {isEmpty(criteria$)
          ? <EmptyTitle
            title={<FormattedMessage {...messages.noCriteria} />}
          />
          : S.map(criterion => S.maybeToNullable(S.map(
            state => (
              <Criteria
                key={`chip-${criterion.property}`}
                label={criterion.label}
                nullable={criterion.nullable}
                criteria={{
                  ...criterion,
                  ...state,
                }}
                onAddConstraint={addConstraint}
                onRemoveConstraint={removeConstraint}
                onClearAll={clearConstraints}
                onOperatorChange={setOperator}
                onValueChange={setValue}
                hasConstraints={hasConstraints(criterion.property)}
                onApply={() => applyCriteria(criterion)}
                onRemove={() => removeCriteria(criterion)}
              />
            ),
            getCriteriaState(criterion.property),
          )), criteria$)
        }
      </ToolbarSection>
      <ToolbarSection align={ToolbarSection.ALIGN_END} shrink>
        <Button
          type="button"
          disabled={isEmpty(criteria$)}
          onClick={() => clear()}
        >
          <FormattedMessage {...messages.clearAll} />
        </Button>
        <MenuWrapper
          right
          floating
          className="advanced-criteria-menu"
          wrappedElement={
            <Button
              type="button"
              className={classnames(
                className,
                'add-criteria-button',
              )}
            >
              <FormattedMessage {...messages.addCriteria} />
              <Icon className="mdc-button__icon">arrow_drop_down</Icon>
            </Button>
          }
        >
          <Menu>
            {S.map(criterion => (
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                }}
                key={criterion.property}
              >
                <CheckboxField
                  id={`criteria-${criterion.property}`}
                  label={criterion.label}
                  labelId={`label-${criterion.property}`}
                  labelPosition={LABEL_AFTER}
                  checked={hasCriteria(criterion.property)}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (e.target.checked) {
                      addCriteria(criterion);
                    } else {
                      removeCriteria(criterion);
                    }
                  }}
                />
              </MenuItem>
            ), criteriaConfig)}
          </Menu>
        </MenuWrapper>
      </ToolbarSection>
    </ToolbarRow>
  );
};

AdvancedSearch.propTypes = {
  criteriaConfig: arrayOf(criteriaShape),
  getCriteriaState: func.isRequired,
  hasCriteria: func.isRequired,
  addCriteria: func.isRequired,
  removeCriteria: func.isRequired,
  applyCriteria: func.isRequired,
  addConstraint: func.isRequired,
  removeConstraint: func.isRequired,
  clear: func.isRequired,
  hasConstraints: func.isRequired,
  clearConstraints: func.isRequired,
  setOperator: func.isRequired,
  setValue: func.isRequired,
};

AdvancedSearch.defaultProps = {
  criteriaConfig: [],
};

export default AdvancedSearch;
